/**
 * Just Carry On — collection page: infinite scroll.
 *
 * The next page loads as the visitor nears the end of the grid: the collection section is
 * asked for with that page's address (Section Rendering API) and its cards go under the
 * grid. A page opened past the first (a refresh, a shared link) offers the earlier pages
 * above it.
 *
 * It stays crawlable: every page keeps its own address (?page=2 …) with Shopify's
 * self-referencing canonical, and the HTML keeps the links to it — Load more is rel="next",
 * Show earlier products is rel="prev", and the page numbers stay for crawlers and anyone
 * without JavaScript. Script only loads those same pages ahead of the visitor and keeps the
 * address bar on the page being read, so a refresh, a shared link or Back lands where they
 * were.
 */
(function () {
  'use strict';

  var JCO = (window.JustCarryOn = window.JustCarryOn || {});
  var strings = JCO.strings || {};
  var $ = function (sel, ctx) { return (ctx || document).querySelector(sel); };
  var $$ = function (sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); };

  var section = $('[data-collection]');
  if (!section) return;
  var sectionId = section.getAttribute('data-section-id');
  var loadMore = $('[data-load-more]', section);
  var grid = $('[data-collection-grid]', section);
  if (!grid || (!loadMore && !$('[data-load-previous]', section))) return;

  /** The collection section rendered for `url`. */
  function fetchPage(url) {
    var u = new URL(url, window.location.href);
    u.searchParams.set('section_id', sectionId);
    return fetch(u.toString())
      .then(function (res) { if (!res.ok) throw new Error('Collection page ' + res.status); return res.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-collection]');
        if (!fresh) throw new Error('Collection page is empty');
        return fresh;
      });
  }

  /** A rendered page's cards, the first marked with its page number. */
  function pageCards(fresh) {
    var freshGrid = $('[data-collection-grid]', fresh);
    if (!freshGrid) return [];
    var cards = $$(':scope > li', freshGrid);
    if (cards[0]) cards[0].setAttribute('data-page-start', freshGrid.getAttribute('data-page'));
    return cards;
  }

  /** Cards that arrived later fade in like the rest, and their prices are converted. */
  function settle(cards) {
    if (JCO.reveal) JCO.reveal(cards);
    else cards.forEach(function (li) { li.removeAttribute('data-reveal'); });
    if (JCO.pricesChanged) JCO.pricesChanged(grid);
  }

  /** Shows the link as loading; returns the function that puts it back. */
  function busy(link) {
    var label = link.textContent;
    link.setAttribute('aria-busy', 'true');
    link.textContent = strings.loading || label;
    return function () { link.textContent = label; link.removeAttribute('aria-busy'); };
  }

  var watcher = null;

  /** The next page's cards go under the grid; the status line and the link move on a page. */
  function loadNext(button, auto) {
    var url = button.getAttribute('href');
    if (!url || button.getAttribute('aria-busy') === 'true') return;
    var done = busy(button);
    fetchPage(url).then(function (fresh) {
      var cards = pageCards(fresh);
      cards.forEach(function (li) { grid.appendChild(li); });
      settle(cards);
      var status = $('.load-more__text', section);
      var freshStatus = $('.load-more__text', fresh);
      if (status && freshStatus) status.textContent = freshStatus.textContent;
      var pagination = $('.pagination', section);
      var freshPagination = $('.pagination', fresh);
      if (pagination && freshPagination) pagination.innerHTML = freshPagination.innerHTML;
      var nextLink = $('[data-load-more-button]', fresh);
      done();
      if (nextLink) button.setAttribute('href', nextLink.getAttribute('href'));
      else button.remove();
      watch();
    }).catch(function () {
      done();
      // Scrolling never navigates away: the link stays for a tap, which may.
      if (!auto) { window.location.href = url; return; }
      button.setAttribute('data-manual', '');
      if (watcher) watcher.unobserve(button);
    });
  }

  /** Opened on a later page: the earlier pages go above the grid, one per tap. */
  function loadPrevious(link) {
    var url = link.getAttribute('href');
    if (!url || link.getAttribute('aria-busy') === 'true') return;
    var done = busy(link);
    fetchPage(url).then(function (fresh) {
      var cards = pageCards(fresh);
      // Keep the visitor's place: the grid grows above them, so the page moves down with it.
      var before = grid.getBoundingClientRect().top;
      var batch = document.createDocumentFragment();
      cards.forEach(function (li) { batch.appendChild(li); });
      grid.insertBefore(batch, grid.firstChild);
      settle(cards);
      window.scrollBy(0, grid.getBoundingClientRect().top - before);
      var earlier = $('[data-load-previous]', fresh);
      done();
      if (earlier) link.setAttribute('href', earlier.getAttribute('href'));
      else link.closest('[data-load-previous-wrap]').remove();
    }).catch(function () { window.location.href = url; });
  }

  section.addEventListener('click', function (event) {
    var more = event.target.closest('[data-load-more-button]');
    var earlier = event.target.closest('[data-load-previous]');
    var link = more || earlier;
    if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    if (more) loadNext(more, false);
    else loadPrevious(earlier);
  });

  // The next page is fetched while the visitor still has a screenful of cards to go.
  if ('IntersectionObserver' in window) {
    watcher = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) { if (entry.isIntersecting) loadNext(entry.target, true); });
    }, { rootMargin: '0px 0px 800px 0px' });
  }

  // Observing afresh after every page re-checks a link still in reach, so a tall screen fills up.
  function watch() {
    if (!watcher) return;
    watcher.disconnect();
    var button = $('[data-load-more-button]', section);
    if (button && !button.hasAttribute('data-manual')) watcher.observe(button);
  }

  /** The address follows the page whose cards fill the top of the screen (page 1 is the bare address). */
  var ticking = false;
  function syncAddress() {
    ticking = false;
    var marks = $$('[data-page-start]', grid);
    if (!marks.length) return;
    var line = window.innerHeight * 0.4;
    var page = marks[0].getAttribute('data-page-start');
    marks.forEach(function (mark) { if (mark.getBoundingClientRect().top < line) page = mark.getAttribute('data-page-start'); });
    var u = new URL(window.location.href);
    if (page === '1') u.searchParams.delete('page');
    else u.searchParams.set('page', page);
    if (u.href !== window.location.href) history.replaceState(history.state, '', u.href);
  }
  window.addEventListener('scroll', function () {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(syncAddress);
  }, { passive: true });

  var first = $(':scope > li', grid);
  if (first) first.setAttribute('data-page-start', grid.getAttribute('data-page'));
  if (loadMore) loadMore.hidden = false;
  watch();
})();
